import { organizationScanAlertsQuerySchema, alertsQuerySchema, alertOccurrencesQuerySchema, alertOccurrencesResponseSchema, alertFilterOptionsQuerySchema, alertFilterOptionsResponseSchema, userScanAlertsQuerySchema, userAlertsFilterOptionsQuerySchema, groupedAlertsResponseSchema, groupedAlertsTableResponseSchema, muteAlertRequestSchema, mutedAlertStateSchema, excludeAlertRequestSchema, excludedAlertStateSchema, bulkAlertActionSchema, bulkAlertApplyRequestSchema, bulkAlertApplyResponseSchema, bulkAlertEligibilityRequestSchema, bulkAlertEligibilityResponseSchema, } from '../contracts/alerts';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
/**
 * List grouped alerts for an organization with pagination, sorting, and search.
 */
export async function listOrganizationGroupedAlerts(client, organizationId, params = {}, options) {
    const validatedParams = parseSchema(organizationScanAlertsQuerySchema, params);
    const response = await client.get(`/organizations/${organizationId}/alerts`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(groupedAlertsResponseSchema, response);
}
/**
 * List individual occurrences for a specific grouped alert.
 */
export async function listAlertOccurrences(client, organizationId, alertKey, params = {}, options) {
    const validatedParams = parseSchema(alertOccurrencesQuerySchema, params);
    const response = await client.get(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/occurrences`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(alertOccurrencesResponseSchema, response);
}
/**
 * List grouped alerts for the authenticated user with pagination and sorting.
 *
 * @deprecated The server now always returns the table-engine shape for
 * GET /account/alerts. Use {@link listUserGroupedDataTableAlerts} instead,
 * which parses the response with the correct {@link groupedAlertsTableResponseSchema}.
 */
export async function listUserGroupedAlerts(client, params = {}) {
    const validatedParams = parseSchema(alertsQuerySchema, params);
    const response = await client.get('/account/alerts', {
        params: validatedParams,
    });
    return parseSchema(groupedAlertsResponseSchema, response);
}
/**
 * List individual occurrences for a specific user grouped alert.
 */
export async function listUserAlertOccurrences(client, alertKey, params = {}) {
    const validatedParams = parseSchema(alertOccurrencesQuerySchema, params);
    const response = await client.get(`/account/alerts/${encodeURIComponent(alertKey)}/occurrences`, { params: validatedParams, paramsSerializer: serializeTableQueryParams });
    return parseSchema(alertOccurrencesResponseSchema, response);
}
/**
 * Get filter options for organization alerts.
 * Returns distinct values for the specified field (severity or users).
 */
export async function getOrganizationAlertFilterOptions(client, organizationId, params) {
    const validatedParams = parseSchema(alertFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${organizationId}/alerts/filter-options`, { params: validatedParams });
    return parseSchema(alertFilterOptionsResponseSchema, response);
}
/**
 * List grouped alerts for the authenticated user with pagination, sorting,
 * filters, and search. Always returns the table engine response shape.
 */
export async function listUserGroupedDataTableAlerts(client, params = {}, options) {
    const validatedParams = parseSchema(userScanAlertsQuerySchema, params);
    const response = await client.get('/account/alerts', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(groupedAlertsTableResponseSchema, response);
}
/**
 * Get filter options for user alerts.
 * Returns distinct values for the specified field (type or filePath).
 */
export async function getUserAlertFilterOptions(client, params) {
    const validatedParams = parseSchema(userAlertsFilterOptionsQuerySchema, params);
    const response = await client.get('/account/alerts/filter-options', {
        params: validatedParams,
    });
    return parseSchema(alertFilterOptionsResponseSchema, response);
}
/**
 * Mute an organization-scoped grouped alert.
 *
 * The alert key is path-encoded so callers can pass arbitrary keys without
 * pre-encoding. The body is validated against {@link muteAlertRequestSchema}
 * before sending. The response is the canonical {@link MutedAlertState}
 * fragment for the alert after the mutation.
 *
 * Routes 404 when the `alert_muting` feature flag is disabled, which is
 * surfaced as a `NotFoundError` from the shared client interceptor.
 */
export async function muteOrganizationAlert(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(muteAlertRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/mute`, validatedBody);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Unmute an organization-scoped grouped alert.
 *
 * Returns the canonical {@link MutedAlertState} fragment after the mutation
 * (with `muted: false` and all timestamps cleared).
 */
export async function unmuteOrganizationAlert(client, organizationId, alertKey) {
    const response = await client.delete(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/mute`);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Mute an account-scoped (self) grouped alert.
 *
 * The alert key is path-encoded; the body is validated against
 * {@link muteAlertRequestSchema}. Returns the canonical
 * {@link MutedAlertState} fragment for the alert after the mutation.
 */
export async function muteAccountAlert(client, alertKey, body) {
    const validatedBody = parseSchema(muteAlertRequestSchema, body);
    const response = await client.post(`/account/alerts/${encodeURIComponent(alertKey)}/mute`, validatedBody);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Unmute an account-scoped (self) grouped alert.
 */
export async function unmuteAccountAlert(client, alertKey) {
    const response = await client.delete(`/account/alerts/${encodeURIComponent(alertKey)}/mute`);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Exclude an organization-scoped grouped alert (KIR-2363).
 *
 * Mirror of {@link muteOrganizationAlert}: path-encoded key, validated
 * body, canonical {@link ExcludedAlertState} response. Routes 404 when the
 * `alert_ignore` feature flag is disabled, surfaced as `NotFoundError`
 * from the shared client interceptor — same shape as mute's flag-off
 * behavior so a single client-side error class handles both.
 */
export async function excludeOrganizationAlert(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(excludeAlertRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/exclude`, validatedBody);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Remove the exclusion from an organization-scoped grouped alert.
 *
 * Returns the canonical {@link ExcludedAlertState} fragment after the
 * mutation (with `excluded: false` and all timestamps cleared).
 */
export async function removeOrganizationAlertExclusion(client, organizationId, alertKey) {
    const response = await client.delete(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/exclude`);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Exclude an account-scoped (self) grouped alert (KIR-2363).
 */
export async function excludeAccountAlert(client, alertKey, body) {
    const validatedBody = parseSchema(excludeAlertRequestSchema, body);
    const response = await client.post(`/account/alerts/${encodeURIComponent(alertKey)}/exclude`, validatedBody);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Remove the exclusion from an account-scoped (self) grouped alert.
 */
export async function removeAccountAlertExclusion(client, alertKey) {
    const response = await client.delete(`/account/alerts/${encodeURIComponent(alertKey)}/exclude`);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Preview eligibility counts for a bulk alert selection (KIR-3252).
 *
 * Drives the bulk-action bar labels and confirmation-modal copy: how many of
 * the selected alerts will change vs. are already in the target state vs. are
 * ineligible. The selection is a descriptor (`keys` | `query`) — `query` lets
 * the backend resolve "all matching the current filter across pages" without
 * the client enumerating ids. Routes 404 when both `alert_muting` and
 * `alert_ignore` flags are off.
 */
export async function getOrganizationBulkAlertEligibility(client, organizationId, body) {
    const validatedBody = parseSchema(bulkAlertEligibilityRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/bulk/eligibility`, validatedBody);
    return parseSchema(bulkAlertEligibilityResponseSchema, response);
}
/**
 * Apply a bulk alert action to an organization selection (KIR-3252).
 *
 * `action ∈ { mute, unmute, exclude, remove-exclusion }`. All-or-nothing: the
 * backend applies the action as a single set-based statement and returns
 * `{ action, changedCount }` — the number of alerts that actually transitioned.
 * Already-in-target-state and exclusion-ineligible alerts are silently skipped
 * (disclosed up front by {@link getOrganizationBulkAlertEligibility}); there is
 * no partial-success result and no undo. Routes 404 when the action's gating
 * flag (`alert_muting` for mute/unmute, `alert_ignore` for exclude/remove) is
 * off.
 */
export async function applyOrganizationBulkAlertAction(client, organizationId, action, body) {
    const validatedAction = parseSchema(bulkAlertActionSchema, action);
    const validatedBody = parseSchema(bulkAlertApplyRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/bulk/${validatedAction}`, validatedBody);
    return parseSchema(bulkAlertApplyResponseSchema, response);
}
/**
 * Preview eligibility counts for a bulk alert selection over the authenticated
 * user's own alerts (KIR-3252, personal scope). Personal-scope twin of
 * {@link getOrganizationBulkAlertEligibility} — same selection descriptor and
 * response shape, no org RBAC. Routes 404 when both `alert_muting` and
 * `alert_ignore` flags are off.
 */
export async function getAccountBulkAlertEligibility(client, body) {
    const validatedBody = parseSchema(bulkAlertEligibilityRequestSchema, body);
    const response = await client.post(`/account/alerts/bulk/eligibility`, validatedBody);
    return parseSchema(bulkAlertEligibilityResponseSchema, response);
}
/**
 * Apply a bulk alert action to the authenticated user's own selection
 * (KIR-3252, personal scope). Personal-scope twin of
 * {@link applyOrganizationBulkAlertAction}: all-or-nothing, returns
 * `{ action, changedCount }`, no partial result and no undo. Routes 404 when
 * the action's gating flag (`alert_muting` for mute/unmute, `alert_ignore` for
 * exclude/remove) is off.
 */
export async function applyAccountBulkAlertAction(client, action, body) {
    const validatedAction = parseSchema(bulkAlertActionSchema, action);
    const validatedBody = parseSchema(bulkAlertApplyRequestSchema, body);
    const response = await client.post(`/account/alerts/bulk/${validatedAction}`, validatedBody);
    return parseSchema(bulkAlertApplyResponseSchema, response);
}
//# sourceMappingURL=alerts.js.map